"use client"

import { useEffect, useRef, useState } from "react"
import { useRouter } from "next/navigation"
import { ChevronLeft } from "lucide-react"
import { Button } from "@/components/ui/button"
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { cn } from "@/lib/utils"
import { useAuth } from "@/lib/auth-context"
import { useEditorStore } from "@/lib/editor-store"
import { useThemeMode } from "@/lib/themes/theme-mode"
import type { AwarenessUserState } from "@/lib/crdt/awareness-user"
import { VersionHistoryPanel } from "@/components/editor/version-history-panel"
import { CookieConsentDialog } from "@/components/editor/cookie-consent-dialog"
import { SharingManagePopup } from "@/components/dashboard/sharing-manage-popup"
import {
  captureAndPersistEditorEntrySource,
  loadEditorEntrySource,
  resolveEditorBackHref,
  resolveEditorLogoHref,
} from "@/lib/editor-entry-source"

const GUEST_FEATURE_TOOLTIP = "Please sign up to use all Rudrise features."
const COMPILE_BLUE = "#5B8CFF"
import { dashboardThemeVars } from "@/lib/themes/dashboard-theme-vars"

function HeaderIconButton({
  src,
  alt,
  disabled,
  tooltip,
  onClick,
  className,
}: {
  src: string
  alt: string
  disabled?: boolean
  tooltip?: string
  onClick?: () => void
  className?: string
}) {
  // save/share/history SVGs already include the rounded border frame
  const button = (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      aria-label={alt}
      className={cn(
        "inline-flex h-[26px] w-[26px] shrink-0 items-center justify-center overflow-hidden rounded-[6px] p-0",
        "hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50",
        className,
      )}
    >
      <img src={src} alt="" draggable={false} className="h-[26px] w-[26px] max-w-none" style={{ filter: "var(--ed-icon-filter, none)" }} />
    </button>
  )

  if (!tooltip) return button

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="inline-flex cursor-not-allowed">{button}</span>
      </TooltipTrigger>
      <TooltipContent
        side="bottom"
        sideOffset={3}
        className={cn(
          "rounded-[4px] border-0 bg-[#323231] px-3 py-2",
          "text-sm font-medium text-white shadow-none",
          "[&>svg]:hidden",
        )}
      >
        {tooltip}
      </TooltipContent>
    </Tooltip>
  )
}

function readAwarenessUser(raw: unknown): AwarenessUserState | null {
  if (!raw || typeof raw !== "object") return null
  const user = raw as Record<string, unknown>
  const name = typeof user.name === "string" ? user.name : ""
  const color = typeof user.color === "string" ? user.color : "#8B5CF6"
  if (!name) return null
  return {
    name,
    color,
    colorLight: typeof user.colorLight === "string" ? user.colorLight : "rgba(139, 92, 246, 0.2)",
    initials: typeof user.initials === "string" ? user.initials : name.slice(0, 1).toUpperCase(),
    username: typeof user.username === "string" ? user.username : undefined,
    avatarUrl: typeof user.avatarUrl === "string" ? user.avatarUrl : undefined,
  }
}

function usePresenceUsers(
  getYjsBinding: ReturnType<typeof useEditorStore>["getYjsBinding"],
  revision: number,
) {
  const [users, setUsers] = useState<AwarenessUserState[]>([])

  useEffect(() => {
    const binding = getYjsBinding()
    if (!binding?.awareness) {
      setUsers([])
      return
    }

    const { awareness } = binding

    const update = () => {
      const next: AwarenessUserState[] = []
      const seen = new Set<string>()
      awareness.getStates().forEach((rawState) => {
        const user = readAwarenessUser((rawState as { user?: unknown })?.user)
        if (!user) return
        const key = user.username || user.name
        if (seen.has(key)) return
        seen.add(key)
        next.push(user)
      })
      setUsers(next.slice(0, 5))
    }

    update()
    awareness.on("update", update)
    return () => {
      awareness.off("update", update)
    }
  }, [getYjsBinding, revision])

  return users
}

function SyncedCloudIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 16 12"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-hidden
    >
      <path
        d="M0 7.81818C0 5.79429 1.43743 4.10716 3.3473 3.72017C3.73568 1.60356 5.58952 0 7.81818 0C10.2806 0 12.2843 1.95801 12.3601 4.40199C14.2122 4.66514 15.6364 6.25705 15.6364 8.18182C15.6364 10.2906 13.9269 12 11.8182 12H4.18182C1.87227 12 0 10.1277 0 7.81818Z"
        fill="currentColor"
      />
      <path
        d="M4.9 6.55L6.85 8.5L11 4.35"
        stroke="white"
        strokeWidth="1.35"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function CompilePlayIcon({ className }: { className?: string }) {
  return (
    <svg
      width="13"
      height="14"
      viewBox="0 0 13 14"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-hidden
    >
      <path
        d="M0 6.97171V2.44189C0 0.565136 2.03202 -0.608858 3.65545 0.330882L7.58057 2.59715L11.5057 4.86343C13.1318 5.80044 13.1318 8.14843 11.5057 9.08545L7.58057 11.3517L3.65545 13.618C2.03202 14.5523 0 13.381 0 11.5043V6.97171Z"
        fill="currentColor"
      />
    </svg>
  )
}

export function EditorHeader() {
  const router = useRouter()
  const historyTriggerRef = useRef<HTMLButtonElement>(null)
  const [historyPanelOpen, setHistoryPanelOpen] = useState(false)
  const [showSharingPopup, setShowSharingPopup] = useState(false)
  const { isAuthenticated } = useAuth()
  const { theme } = useThemeMode()
  const isDark = theme.name === "dark" || theme.name === "gray"
  const {
    state,
    projectName,
    projectId,
    manualCompile,
    getYjsBinding,
  } = useEditorStore()

  // Most editor components still use Tailwind `dark:` classes (being migrated to
  // --ed-* CSS vars file by file) — keep toggling the `.dark` class until that's done.
  useEffect(() => {
    document.documentElement.classList.toggle("dark", isDark)
  }, [isDark])

  const guestRestricted = !isAuthenticated
  const presenceUsers = usePresenceUsers(getYjsBinding, state.crdtProviderEpoch)

  const canCompile =
    Boolean(state.sessionId) &&
    state.wsStatus === "connected" &&
    state.compileStatus !== "compiling"

  const isSynced = state.wsStatus === "connected" && state.crdtProviderEpoch > 0
  const syncLabel =
    state.wsStatus === "reconnecting"
      ? "Syncing"
      : state.wsStatus === "connected"
        ? isSynced || Boolean(state.sessionId)
          ? "Synced"
          : "Syncing"
        : "Offline"

  const displayName = projectName?.trim() || "Project"
  const projectPath = `\\Rudrise\\Projects\\${displayName}`

  useEffect(() => {
    captureAndPersistEditorEntrySource({
      projectId,
      isAuthenticated,
      pageHref: window.location.href,
      referrer: document.referrer,
    })
  }, [projectId, isAuthenticated])

  const goToLogoDestination = () => {
    router.push(resolveEditorLogoHref(isAuthenticated))
  }

  const goToBackDestination = () => {
    router.push(resolveEditorBackHref(loadEditorEntrySource(), isAuthenticated))
  }

  return (
    <>
      <header
        className={cn(
          "relative flex h-[45px] shrink-0 select-none items-center",
          "border-b border-[var(--ed-header-border)] bg-[var(--ed-header-bg)]",
          "px-2 text-sm",
        )}
        style={dashboardThemeVars(theme)}
      >
        {/* LEFT */}
        <div className="z-10 flex shrink-0 items-center gap-2">
          <button
            type="button"
            onClick={goToLogoDestination}
            aria-label="Rudrise home"
            className="flex h-7 w-7 items-center justify-center rounded-[6px] hover:opacity-90"
          >
            <img
              src="/rudrise-logo.svg"
              alt=""
              draggable={false}
              className="h-7 w-auto object-contain"
            />
          </button>

          <Button
              type="button"
              variant="ghost"
              onClick={goToBackDestination}
              className={cn(
                  "h-[26px] gap-0.5 rounded-[6px] px-2 py-0 border",
                  "border-[var(--db-border)] bg-[var(--db-canvas-bg)]",
                  "text-[13px] font-medium text-[var(--db-text-primary)]",
                  "hover:bg-[var(--db-surface-hover)] hover:text-[var(--db-user-header)]",
                  "transition-colors",
              )}
          >
            <ChevronLeft className="h-3.5 w-3.5 shrink-0" strokeWidth={2.25} />
            Back
          </Button>

          <HeaderIconButton
            src="/save.svg"
            alt="Save"
            disabled={guestRestricted}
            tooltip={guestRestricted ? GUEST_FEATURE_TOOLTIP : undefined}
          />

          <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={manualCompile}
              disabled={!canCompile}
              className={cn(
                  "h-[26px] gap-1.5 rounded-[6px] border border-solid px-3.5 shadow-none",
                  "bg-[var(--ed-header-compile-bg)] font-medium text-[13px] leading-none",
                  "hover:bg-[var(--ed-header-compile-hover)] hover:text-[#5B8CFF] active:bg-[var(--ed-header-compile-active)]",
                  "disabled:opacity-50",
              )}
              style={{
                borderColor: "var(--db-user-header)",
              }}
          >
            {state.compileStatus === "compiling" ? (
                <span className="size-3.5 animate-spin rounded-full border-2 border-current border-t-transparent" />
            ) : (
                <CompilePlayIcon className="size-3.5" />
            )}
            Compile
          </Button>
        </div>

        {/* CENTER — absolute so it stays truly centered */}
        <div className="pointer-events-none absolute inset-x-0 flex items-center justify-center px-48">
          <p className="truncate text-[13px] leading-none text-[var(--ed-header-path-text)]">
            <span className="font-semibold text-[var(--ed-header-title-text)]">
              {displayName}
            </span>
            <span className="font-normal">
              {" - "}
              {projectPath}
            </span>
          </p>
        </div>

        {/* RIGHT */}
        <div className="z-10 ml-auto flex shrink-0 items-center gap-2">
          <HeaderIconButton
            src="/share.svg"
            alt="Share"
            disabled={guestRestricted}
            tooltip={guestRestricted ? GUEST_FEATURE_TOOLTIP : undefined}
            onClick={() => setShowSharingPopup(true)}
          />

          {presenceUsers.length > 0 && (
            <div className="flex items-center">
              {presenceUsers.map((user, index) => (
                <Avatar
                  key={`${user.username || user.name}-${index}`}
                  className={cn(
                    "h-6 w-6 border-2 bg-[var(--ed-header-avatar-bg)]",
                    index > 0 && "-ml-2",
                  )}
                  style={{ borderColor: user.color, zIndex: index + 1 }}
                  title={user.name}
                >
                  {user.avatarUrl ? (
                    <AvatarImage src={user.avatarUrl} alt={user.name} />
                  ) : null}
                  <AvatarFallback
                    className="text-[10px] font-semibold text-white"
                    style={{ backgroundColor: user.color }}
                  >
                    {(user.initials || user.name).slice(0, 1).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
              ))}
            </div>
          )}

          <div
            className={cn(
              "flex items-center gap-1 text-[13px] font-medium leading-none",
              syncLabel === "Synced"
                ? "text-[#10B981]"
                : syncLabel === "Syncing"
                  ? "text-amber-500"
                  : "text-gray-400",
            )}
            title={
              state.wsStatus === "connected"
                ? "Connected and synced"
                : `Connection: ${state.wsStatus}`
            }
          >
            <SyncedCloudIcon
              className={cn(
                "h-3.5 w-[18px]",
                syncLabel === "Synced"
                  ? "text-[#10B981]"
                  : syncLabel === "Syncing"
                    ? "text-amber-500"
                    : "text-gray-400",
              )}
            />
            <span>{syncLabel}</span>
          </div>

          <div className="w-2 shrink-0" aria-hidden />

          <button
            ref={historyTriggerRef}
            type="button"
            aria-expanded={historyPanelOpen}
            aria-controls="version-history-panel"
            aria-haspopup="dialog"
            title={historyPanelOpen ? "Hide version history" : "Show version history"}
            className={cn(
              "inline-flex h-[26px] w-[26px] shrink-0 items-center justify-center overflow-hidden rounded-[6px] p-0",
              "hover:opacity-90",
              historyPanelOpen && "ring-2 ring-[#5B8CFF]/35",
            )}
            onClick={() => setHistoryPanelOpen((v) => !v)}
          >
            <img src="/history.svg" alt="" draggable={false} className="h-[26px] w-[26px] max-w-none" style={{ filter: "var(--ed-icon-filter, none)" }} />
            <span className="sr-only">Version history</span>
          </button>
        </div>
      </header>

      <VersionHistoryPanel
        open={historyPanelOpen}
        onOpenChange={setHistoryPanelOpen}
        triggerRef={historyTriggerRef}
        historyUrl={state.crdtHistoryUrl}
        sessionId={state.sessionId}
        replaySeedFiles={state.crdtReplaySeedFiles}
        mainFilePath={state.mainFile}
        projectId={projectId}
      />
      <CookieConsentDialog />

      {showSharingPopup && projectId && (
        <SharingManagePopup
          resourceId={projectId}
          resourceName={projectName || "Project"}
          resourceKind="folder"
          iconSrc="/ic_folder.svg"
          onClose={() => setShowSharingPopup(false)}
          projectId={projectId}
        />
      )}
    </>
  )
}
